// pages/config/OverlayPreviews.jsx
// Overlay Preview Hub — one place to eyeball every /overlay/* scene at a glance.
//
// Why this exists: the per-scene Preview / Copy URL buttons already live inside
// StreamSetup's "Scenes" section, but they're buried in a long config page and
// impossible to find. This is a dedicated browse page reachable from the staff
// menu (Stream → Overlay Previews): a grid of LIVE scaled-down thumbnails, each
// with 👁 Portrait / 👁 Landscape / Copy URL actions.
//
// Portrait-first: the rig outputs vertical (1080×1920) to both the in-person
// screen and the livestream, so thumbnails render portrait by default with a
// landscape toggle for the legacy 16:9 cut.
//
// Perf note: each thumbnail is a real <iframe> of the overlay route, so it runs
// the overlay's Firestore onSnapshot listeners. Tiles lazy-mount via
// IntersectionObserver — off-screen scenes don't load until scrolled near, and
// the page is a brief staff visit, not a long-running OBS source.
import { useEffect, useMemo, useRef, useState } from "react";
import useActiveShift from "./../../hooks/useActiveShift";
import { normalizeJoinCode, shiftLabel } from "./../../utils/shiftScope";
import { BOOTH_PRESETS, presetLabelFor } from "./../../constants/booths";
import { OVERLAY_SCENES } from "../../utils/alerts";
import { STAFF_THEME, FONT } from "../../theme/staff";

const T = STAFF_THEME;

// Logical render size for each overlay iframe (canonical stream canvas per
// orientation), scaled down into the thumbnail box below. Portrait thumbs are
// narrower so a row still fits several scenes.
const FRAME_DIMS = {
  portrait:  { frameW: 1080, frameH: 1920, thumbW: 200 },
  landscape: { frameW: 1280, frameH: 720,  thumbW: 320 },
};

// Transition + title-card routes that exist in the router but aren't part of the
// curated OVERLAY_SCENES config set. Listed here so the hub covers every
// /overlay/* route, not just the per-scene-config ones.
const EXTRA_SCENES = [
  { id: "stinger",        label: "Stinger",          path: "/overlay/stinger",        description: "480ms wipe transition played between scene switches. Renders portrait or landscape from the viewport (or ?portrait)." },
  { id: "titlecard",      label: "Title Card",       path: "/overlay/titlecard",      description: "Generic title card. Landscape-only (baked canvas)." },
  { id: "titlecard-gts",  label: "Title Card — GTS",  path: "/overlay/titlecard-gts",  description: "Golden Ticket System title variant. Landscape-only (baked canvas)." },
  { id: "titlecard-stat", label: "Title Card — Stat", path: "/overlay/titlecard-stat", description: "Stat title variant. Landscape-only (baked canvas)." },
  { id: "titlecard-end",  label: "Title Card — End",  path: "/overlay/titlecard-end",  description: "End-screen title variant. Landscape-only (baked canvas)." },
];

// Checkerboard backdrop so transparent overlay regions read as transparent
// (overlays composite over the camera feed in OBS — there's no real background).
const CHECKER = {
  backgroundColor: "#262626",
  backgroundImage:
    "linear-gradient(45deg, #1c1c1c 25%, transparent 25%), linear-gradient(-45deg, #1c1c1c 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1c1c1c 75%), linear-gradient(-45deg, transparent 75%, #1c1c1c 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
};

function openPreview(scene, orientation) {
  const w = orientation === "portrait" ? 540 : 960;
  const h = orientation === "portrait" ? 960 : 540;
  const features = `width=${w},height=${h},left=${Math.max(0, (window.screen.availWidth - w) / 2)},top=${Math.max(0, (window.screen.availHeight - h) / 2)},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`;
  // Mirror StreamSetup: alerts scene gets ?debug so the corner HUD shows status.
  const url = scene.id === "alerts" ? `${scene.path}?debug` : scene.path;
  window.open(url, `gts_preview_${scene.id}_${orientation}`, features);
}

function PreviewTile({ scene, orientation, live = true, boothCode = "" }) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  // A rig's browser-source URL names the booth it belongs to (?s=CODE), so the
  // copied URL has to carry it — hand-editing eight sources at an event is
  // exactly where a wrong booth slips in. See utils/shiftScope.
  const fullUrl = useMemo(() => {
    const base = `${window.location.origin}${scene.path}`;
    return boothCode ? `${base}?s=${boothCode}` : base;
  }, [scene.path, boothCode]);

  const { frameW, frameH, thumbW } = FRAME_DIMS[orientation];
  const thumbH = (thumbW * frameH) / frameW;
  const scale = thumbW / frameW;

  // Lazy-mount the iframe only when the tile scrolls near the viewport, so
  // off-screen overlays never spin up Firestore listeners. Skipped entirely for
  // non-live tiles (transitions / title cards) — those autoplay animation + sound
  // on mount, which is obnoxious in a thumbnail. They preview on demand instead.
  useEffect(() => {
    const el = ref.current;
    if (!el || visible || !live) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  function copy() {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      }}
    >
      {/* Thumbnail — click anywhere to open the full preview window in the
          hub's current orientation */}
      <div
        ref={ref}
        onClick={() => openPreview(scene, orientation)}
        title={`Open ${orientation} preview`}
        style={{
          position: "relative",
          width: "100%",
          height: thumbH,
          cursor: "pointer",
          ...CHECKER,
        }}
      >
        {!live ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: T.muted, fontSize: "0.82rem", gap: "0.3rem" }}>
            <div style={{ fontSize: "1.4rem" }}>▶</div>
            <div>Tap to preview</div>
          </div>
        ) : visible ? (
          <iframe
            title={`${scene.label} preview`}
            src={scene.path}
            tabIndex={-1}
            scrolling="no"
            style={{
              width: frameW,
              height: frameH,
              border: "none",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              pointerEvents: "none",
              // Center the scaled frame horizontally in the tile (portrait
              // frames are narrower than the tile at grid minmax widths)
              position: "absolute",
              left: "50%",
              marginLeft: -(frameW * scale) / 2,
            }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.dim, fontSize: "0.8rem" }}>
            loading…
          </div>
        )}
        {/* Hover affordance */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", boxShadow: `inset 0 0 0 2px transparent` }} />
      </div>

      {/* Label + path */}
      <div style={{ padding: "0.6rem 0.75rem 0.7rem" }}>
        <div style={{ color: "white", fontSize: "0.92rem", fontWeight: 600, fontFamily: FONT.display }}>{scene.label}</div>
        <div style={{ color: T.sky, fontSize: "0.7rem", fontFamily: "monospace", marginTop: "1px" }}>{scene.path}</div>
        {scene.description && (
          <div style={{ color: T.dim, fontSize: "0.72rem", marginTop: "0.35rem", lineHeight: 1.4 }}>{scene.description}</div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.6rem", flexWrap: "wrap" }}>
          <button onClick={() => openPreview(scene, "portrait")} style={actionBtn(T.sky)} title="Preview at 540×960 (portrait)">👁 Portrait</button>
          <button onClick={() => openPreview(scene, "landscape")} style={actionBtn(T.sky)} title="Preview at 960×540 (landscape)">👁 Landscape</button>
          <button onClick={copy} style={actionBtn(copied ? T.green : T.muted)}>{copied ? "✓ Copied" : "Copy URL"}</button>
        </div>
      </div>
    </div>
  );
}

function actionBtn(color) {
  return {
    background: "none",
    border: `1px solid ${T.border}`,
    borderRadius: "6px",
    color,
    fontSize: "0.76rem",
    fontWeight: 600,
    padding: "0.35rem 0.6rem",
    cursor: "pointer",
    fontFamily: FONT.display,
    lineHeight: 1,
  };
}

function SceneGrid({ scenes, orientation, live = true, boothCode = "" }) {
  const { thumbW } = FRAME_DIMS[orientation];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${thumbW}px, 1fr))`,
        gap: "1rem",
      }}
    >
      {scenes.map((s) => (
        <PreviewTile key={s.id} scene={s} orientation={orientation} live={live} boothCode={boothCode} />
      ))}
    </div>
  );
}

function OrientationToggle({ orientation, onChange }) {
  const opts = [
    { id: "portrait",  label: "▯ Portrait 9:16" },
    { id: "landscape", label: "▭ Landscape 16:9" },
  ];
  return (
    <div style={{ display: "inline-flex", gap: "0.35rem", background: T.card, border: `1px solid ${T.border}`, borderRadius: "10px", padding: "0.3rem" }}>
      {opts.map((o) => {
        const active = o.id === orientation;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              background: active ? T.sky : "none",
              color: active ? "#0d1b2a" : T.muted,
              border: "none",
              borderRadius: "7px",
              fontSize: "0.82rem",
              fontWeight: 700,
              fontFamily: FONT.display,
              padding: "0.45rem 0.8rem",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── BOOTH SCOPE BAR ─────────────────────────────────────────────────────────
// Sets which booth every copied URL points at. Only earns its space when two
// events can collide — but it's always visible because rigs get built the
// night BEFORE a shift exists, when there's no live booth to infer from.
function BoothCodeBar({ code, onChange, normalized, candidates }) {
  const [copiedAll, setCopiedAll] = useState(false);

  function copyAll() {
    const lines = OVERLAY_SCENES.filter(sc => sc.built !== false).map(sc => {
      const base = `${window.location.origin}${sc.path}`;
      return `${sc.label}\t${normalized ? `${base}?s=${normalized}` : base}`;
    });
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2200);
    });
  }

  return (
    <div style={{
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.75rem",
      padding: "0.8rem 1rem", margin: "0 0 1.25rem",
      background: "rgba(252,179,31,0.07)",
      border: "1px solid rgba(252,179,31,0.3)",
      borderRadius: "12px",
    }}>
      <span style={{ color: T.gold, fontFamily: FONT.display, fontSize: "0.85rem", fontWeight: 700 }}>
        📍 Booth code
      </span>
      <input
        value={code}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder="NONE"
        maxLength={4}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "6rem", padding: "0.45rem 0.6rem",
          background: "rgba(0,0,0,0.25)", border: `1px solid ${T.border}`,
          borderRadius: "8px", color: "#fff",
          fontFamily: "monospace", fontSize: "1rem",
          letterSpacing: "0.25em", textAlign: "center",
        }}
      />
      {/* Stations first — a rig is usually configured BEFORE its shift is
          open, which is exactly when there are no live booths to click. */}
      {BOOTH_PRESETS.map(b => (
        <button
          key={b.code}
          onClick={() => onChange(b.code)}
          style={{
            padding: "0.35rem 0.7rem", borderRadius: "999px",
            background: normalized === b.code ? "rgba(252,179,31,0.25)" : "transparent",
            border: `1px solid ${normalized === b.code ? T.gold : T.border}`,
            color: normalized === b.code ? T.gold : T.muted,
            fontFamily: FONT.display, fontSize: "0.78rem", cursor: "pointer",
          }}
        >{b.label}</button>
      ))}
      {/* Live booths whose code isn't one of the presets (a one-off rig). */}
      {candidates.map(sh => sh.joinCode && !presetLabelFor(sh.joinCode) && (
        <button
          key={sh.id}
          onClick={() => onChange(sh.joinCode)}
          style={{
            padding: "0.35rem 0.7rem", borderRadius: "999px",
            background: normalized === sh.joinCode ? "rgba(252,179,31,0.25)" : "transparent",
            border: `1px solid ${normalized === sh.joinCode ? T.gold : T.border}`,
            color: normalized === sh.joinCode ? T.gold : T.muted,
            fontFamily: FONT.display, fontSize: "0.78rem", cursor: "pointer",
          }}
        >{sh.joinCode} · {shiftLabel(sh)}</button>
      ))}
      <div style={{ flex: "1 1 240px", minWidth: 0, color: T.muted, fontSize: "0.76rem", lineHeight: 1.4 }}>
        {normalized
          ? <>Each <strong>Copy URL</strong> below now ends in <code style={{ color: T.gold }}>?s={normalized}</code> — add them to that rig's OBS sources one at a time. This box assigns nothing on its own: a shift only answers to <code style={{ color: T.gold }}>{normalized}</code> if it was opened with that Booth code.</>
          : <>Blank = unscoped URLs (fine when only one booth runs at a time). Type the Booth code a rig's shifts are opened with to scope its sources.</>}
      </div>
      <button onClick={copyAll} style={actionBtn(copiedAll ? T.green : T.gold)}>
        {copiedAll ? "✓ Copied as a list" : "Copy list (for notes)"}
      </button>
    </div>
  );
}

export default function OverlayPreviews() {
  // Portrait is the rig's default output — vertical screen + vertical stream.
  const [orientation, setOrientation] = useState("portrait");

  // Booth scoping for the copied URLs. Defaults to whatever booth is live
  // (or this device's, when several are), so the common case needs no thought;
  // a free-text box covers setting a rig up BEFORE its shift exists, which is
  // the normal order of operations the night before an event.
  const { candidates } = useActiveShift();
  const [boothCodeRaw, setBoothCodeRaw] = useState("");
  const boothCode = normalizeJoinCode(boothCodeRaw);
  useEffect(() => {
    if (boothCodeRaw) return;              // never fight an explicit choice
    if (candidates.length !== 1) return;   // ambiguous → make them pick
    const code = candidates[0]?.joinCode;
    if (code) setBoothCodeRaw(code);
  }, [candidates, boothCodeRaw]);

  // Only show built scenes from the curated set; the extras are all live routes.
  const builtScenes = OVERLAY_SCENES.filter((s) => s.built !== false);

  return (
    <div style={{ padding: "1.25rem 1.25rem 2.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "1rem", margin: "0 0 1.25rem" }}>
        <p style={{ color: T.muted, fontSize: "0.85rem", lineHeight: 1.5, margin: 0, flex: "1 1 380px", maxWidth: "640px" }}>
          Live previews of every OBS overlay scene, rendered at the rig's vertical (9:16) output by
          default. Tap a thumbnail (or 👁) to pop out a full-size preview window, or Copy URL to paste
          the scene into an OBS Browser Source — the scene adapts to whatever size the source is set
          to. Checkerboard = transparent (the camera feed shows through there in OBS).
        </p>
        <OrientationToggle orientation={orientation} onChange={setOrientation} />
      </div>

      <BoothCodeBar
        code={boothCodeRaw}
        onChange={setBoothCodeRaw}
        normalized={boothCode}
        candidates={candidates}
      />

      <h2 style={sectionHeading}>Scenes</h2>
      <SceneGrid scenes={builtScenes} orientation={orientation} boothCode={boothCode} />

      <h2 style={{ ...sectionHeading, marginTop: "2rem" }}>Transitions & Title Cards</h2>
      <SceneGrid scenes={EXTRA_SCENES} orientation={orientation} live={false} boothCode={boothCode} />
    </div>
  );
}

const sectionHeading = {
  color: "rgba(255,218,173,0.7)",
  fontFamily: FONT.display,
  fontSize: "0.8rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  margin: "0 0 0.85rem",
};
